/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, cssMap } from '@atlaskit/css';
import Skeleton from '@atlaskit/skeleton';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { EmbedHeaderSkeleton } from './EmbedHeaderSkeleton.tsx';

/**
 * Skeleton for the request-types embedded view.
 *
 * Mirrors the layout of RequestTypesView + its Container:
 *   - same outer container styles (flex column, elevation.surface bg)
 *   - EmbedHeaderSkeleton (the real EmbedHeader lives inside the Placeholder)
 *   - Filter / search bar
 *   - Two request type groups with rows
 */

const loadingMessage = defineMessage({
	id: 'servicedesk-solution-composer.request-types-skeleton.loading',
	defaultMessage: 'Loading request types',
	description: 'Aria label for the request types skeleton',
});

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%',
		minWidth: '640px',
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface'),
	},
	content: {
		paddingInline: token('space.400'),
		paddingBlockStart: token('space.300'),
	},
	sectionLabel: {
		paddingBlockStart: token('space.300'),
		paddingBlockEnd: token('space.100'),
	},
	row: {
		paddingBlock: token('space.150'),
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: token('color.border'),
	},
});

const RequestTypeRow = () => (
	<Box xcss={styles.row} aria-hidden="true">
		<Inline space="space.150" alignBlock="center" spread="space-between">
			<Inline space="space.150" alignBlock="center">
				<Skeleton
					width={token('space.400')}
					height={token('space.400')}
					borderRadius={token('radius.small')}
					isShimmering
				/>
				<Stack space="space.050">
					<Skeleton
						width={200}
						height={token('space.200')}
						borderRadius={token('radius.xsmall')}
						isShimmering
					/>
					<Skeleton
						width={140}
						height={token('space.150')}
						borderRadius={token('radius.xsmall')}
						isShimmering
					/>
				</Stack>
			</Inline>
			<Inline space="space.100" alignBlock="center">
				<Skeleton width={60} height={28} borderRadius={token('radius.medium')} isShimmering />
				<Skeleton width={28} height={28} borderRadius={token('radius.medium')} isShimmering />
			</Inline>
		</Inline>
	</Box>
);

export const RequestTypesSkeleton = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={styles.container}
			role="status"
			aria-busy="true"
			aria-label={formatMessage(loadingMessage)}
		>
			<EmbedHeaderSkeleton />

			<Box xcss={styles.content}>
				{/* Filter / search bar */}
				<Inline spread="space-between" alignBlock="center" aria-hidden="true">
					<Skeleton
						width={260}
						height={token('space.400')}
						borderRadius={token('radius.medium')}
						isShimmering
					/>
					<Inline space="space.100">
						<Skeleton
							width={90}
							height={token('space.400')}
							borderRadius={token('radius.medium')}
							isShimmering
						/>
						<Skeleton
							width={130}
							height={token('space.400')}
							borderRadius={token('radius.medium')}
							isShimmering
						/>
					</Inline>
				</Inline>

				{/* Section 1 */}
				<Box xcss={styles.sectionLabel} aria-hidden="true">
					<Skeleton
						width={token('space.1000')}
						height={token('space.200')}
						borderRadius={token('radius.xsmall')}
						isShimmering
					/>
				</Box>
				{[...Array(3)].map((_, i) => (
					<RequestTypeRow key={i} />
				))}

				{/* Section 2 */}
				<Box xcss={styles.sectionLabel} aria-hidden="true">
					<Skeleton
						width={token('space.800')}
						height={token('space.200')}
						borderRadius={token('radius.xsmall')}
						isShimmering
					/>
				</Box>
				{[...Array(4)].map((_, i) => (
					<RequestTypeRow key={i} />
				))}
			</Box>
		</Box>
	);
};
